import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { Button, Card, ProgressBar, Row, Col } from 'react-bootstrap';
import {
    BsTelephoneFill as PhoneIcon,
    BsPersonFill as PersonIcon,
    BsClockFill as ClockIcon,
    BsPlayFill as PlayIcon,
    BsStopFill as StopIcon,
    BsArrowRepeat as RefreshIcon,
    BsEye,
} from 'react-icons/bs';
import styled from '@emotion/styled';
import { getTeamIdFromURL } from '@/_helpers';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { fetchCallsAction, setActiveCall } from '../../../../../_redux/actions/ActionCallCenter';
import { VscLoading } from 'react-icons/vsc';
import ActiveCallModal from './components/ActiveCallModal';
import CallCenterCampaignQueue from './components/CallCenterCampaignQueue';
const AUTO_REFRESH_INTERVAL = 10000;

const StyledCalledTab = styled.div`
    .calls-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .call-card {
        border: none;
        border-radius: 12px;
        overflow: hidden;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);

        &:hover {
            transform: translateY(-4px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1rem 1.25rem;

            h5 {
                margin: 0;
                font-weight: 600;
                display: flex;
                align-items: center;
                gap: 8px;
            }
        }

        .call-status-badge {
            font-size: 0.75rem;
            padding: 0.35em 0.65em;
            border-radius: 20px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 600;

            &.status-in_progress {
                background: rgba(40, 167, 69, 0.15);
                color: #28a745;
                animation: pulse 2s infinite;
            }

            &.status-ringing {
                background: rgba(255, 193, 7, 0.15);
                color: #ffc107;
                animation: pulse 1.5s infinite;
            }

            &.status-completed {
                background: rgba(108, 117, 125, 0.15);
                color: #6c757d;
            }

            &.status-failed {
                background: rgba(220, 53, 69, 0.15);
                color: #dc3545;
            }
        }

        .contact-info {
            padding: 1.5rem;

            .contact-name {
                font-weight: 600;
                margin-bottom: 0.5rem;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .contact-phone {
                color: #6c757d;
                margin-bottom: 0.5rem;
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .contact-company {
                color: #495057;
                margin-bottom: 1rem;
            }
        }

        .call-details {
            background: #f8f9fa;
            padding: 1rem 1.5rem;
            border-top: 1px solid #e9ecef;

            .detail-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 0.5rem;

                &:last-child {
                    margin-bottom: 0;
                }

                .detail-label {
                    font-size: 1.4rem;
                    color: #6c757d;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .detail-value {
                    font-size: 1.4rem;

                    font-weight: 500;
                    color: #343a40;
                }
            }
        }

        .call-actions {
            padding: 1rem 1.5rem;
            border-top: 1px solid #e9ecef;
            display: flex;
            gap: 10px;

            .action-btn {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 6px;
                padding: 0.5rem;
                border-radius: 8px;
                font-weight: 500;
                transition: all 0.2s ease;

                &:hover {
                    transform: translateY(-2px);
                }

                &.btn-listen {
                    background: rgba(40, 167, 69, 0.1);
                    color: #28a745;
                    border: 1px solid rgba(40, 167, 69, 0.2);

                    &:hover {
                        background: rgba(40, 167, 69, 0.2);
                    }
                }

                &.btn-barge {
                    background: rgba(255, 193, 7, 0.1);
                    color: #ffc107;
                    border: 1px solid rgba(255, 193, 7, 0.2);

                    &:hover {
                        background: rgba(255, 193, 7, 0.2);
                    }
                }
            }
        }

        .call-progress {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: #e9ecef;
            overflow: hidden;

            .progress-bar {
                background: linear-gradient(90deg, #28a745, #20c997);
                animation: progress-animation 2s ease-in-out infinite;

                @keyframes progress-animation {
                    0% {
                        transform: translateX(-100%);
                    }
                    100% {
                        transform: translateX(100%);
                    }
                }
            }
        }

        .duration-timer {
            position: absolute;
            top: 1rem;
            right: 1.5rem;
            background: rgba(255, 255, 255, 0.9);
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.85rem;
            font-weight: 600;
            color: #343a40;
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4rem 2rem;
        text-align: center;

        .empty-icon {
            font-size: 4rem;
            color: #dee2e6;
            margin-bottom: 1rem;
        }

        h4 {
            color: #6c757d;
            margin-bottom: 0.5rem;
        }

        p {
            color: #adb5bd;
            margin-bottom: 1.5rem;
        }
    }

    .refresh-controls {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
    }

    @keyframes pulse {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0.5;
        }
    }
`;

const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const CallCard = ({ call, onView, onListen, onBarge, isRefreshing }) => {
    const [duration, setDuration] = useState(call.duration || 0);
    const isActive = call.callStatus === 'in_progress' || call.callStatus === 'ringing';
    const contactData = call?.contactData || call?.contact;
    const presetData = call?.presetData || call?.preset;
    const dialedByUserData = call?.dialedByUserData || call?.dialedByUser;

    useEffect(() => {
        let interval;
        if (isActive && call.startedAt) {
            const startTime = new Date(call.startedAt);
            const updateDuration = () => {
                const now = new Date();
                const newDuration = Math.floor((now - startTime) / 1000);
                setDuration(newDuration);
            };

            updateDuration();
            interval = setInterval(updateDuration, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isActive, call.startedAt]);

    const getStatusIcon = () => {
        switch (call.callStatus) {
            case 'in_progress':
                return <PlayIcon />;
            case 'ringing':
                return <PhoneIcon />;
            case 'completed':
            case 'failed':
                return <StopIcon />;
            default:
                return <PhoneIcon />;
        }
    };

    return (
        <Card className="call-card">
            {isActive && (
                <div className="call-progress">
                    <ProgressBar now={100} animated />
                </div>
            )}

            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        {getStatusIcon()}
                        <span className="text-uppercase ml-2">{call.callStatus?.replace('_', ' ') || ''}</span>
                    </div>
                </div>
            </Card.Header>

            <div className="contact-info">
                <div className="contact-name">
                    <PersonIcon />
                    {contactData?.name || 'Unknown Contact'}
                </div>
                <div className="contact-phone">
                    <PhoneIcon />
                    {contactData?.phone || 'No phone number'}
                </div>
            </div>

            <div className="call-details">
                {call?.presetData && (
                    <div className="detail-item">
                        <span className="detail-label">Preset</span>
                        <span className="detail-value">{presetData.title}</span>
                    </div>
                )}

                {call?.dialedByUserData && (
                    <div className="detail-item">
                        <span className="detail-label">Dialed By</span>
                        <span className="detail-value">{dialedByUserData?.name}</span>
                    </div>
                )}
            </div>

            <div className="call-actions">
                <Button variant="primary" className="action-btn btn-view" onClick={() => onView(call)}>
                    <BsEye />
                    View Call
                </Button>
            </div>

            {isActive && (
                <div className="duration-timer">
                    <ClockIcon />
                    {formatDuration(duration)}
                </div>
            )}
        </Card>
    );
};

const Component = ({
    setActiveCall,
    fetchCallsAction,
    onListenIn,
    onBargeIn,
    onHangup,
    listenLoading,
    bargeLoading,
    hangupLoading,
    teams,
}) => {
    const teamId = getTeamIdFromURL();
    const [calls, setCalls] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [showActiveCallModal, setShowActiveCallModal] = useState(false);
    const [activeCallId, setActiveCallId] = useState(null);
    const fetchLiveCalls = useCallback(async (showLoading = true) => {
        if (loading) return;
        if (showLoading) setLoading(true);
        setRefreshing(true);

        try {
            const startDate = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString();
            const response = await fetchCallsAction(teamId, {
                limit: 50,
                active: true,
                startDate,
                teams,
            });
            setCalls(response?.results || []);
        } catch (error) {
            console.error('Error fetching calls:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        fetchLiveCalls();
    }, [fetchLiveCalls]);

    // Auto-refresh every 20 seconds
    useEffect(() => {
        let interval;
        interval = setInterval(() => {
            fetchLiveCalls(false);
        }, AUTO_REFRESH_INTERVAL);
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [fetchLiveCalls]);

    const handleViewCall = (call) => {
        const callId = call?._id;
        if (!callId) return;
        setActiveCall(call);
        setActiveCallId(callId);
        setShowActiveCallModal(true);
    };

    const handleManualRefresh = () => {
        fetchLiveCalls();
    };

    return (
        <StyledCalledTab>
            <Row>
                <Col lg={8}>
                    <Card className="h-100">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    Active Calls: <span>{(calls || []).length}</span>
                                </h5>

                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleManualRefresh}
                                    disabled={refreshing}
                                >
                                    <RefreshIcon className={`${refreshing ? 'spin' : ''}`} />
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ overflowY: 'auto' }}>
                            {loading && calls && calls.length === 0 ? (
                                <div className="text-center py-5">
                                    <VscLoading className="spin" size={24} />
                                    <p className="mt-3">Loading active calls...</p>
                                </div>
                            ) : (
                                <>
                                    {calls && calls.length > 0 && (
                                        <div className="mb-4">
                                            <div className="calls-grid">
                                                {calls.map((call) => (
                                                    <CallCard
                                                        key={call.id || call._id}
                                                        call={call}
                                                        onView={handleViewCall}
                                                        isRefreshing={refreshing}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {calls && calls.length === 0 && !loading && (
                                        <div className="empty-state">
                                            <PhoneIcon className="empty-icon" />
                                            <h4>No Calls Found</h4>
                                            <p>There are no active or recent calls to display.</p>
                                            <Button
                                                variant="primary"
                                                onClick={handleManualRefresh}
                                                disabled={refreshing}
                                            >
                                                <RefreshIcon className={refreshing ? 'refresh-spinner me-2' : 'me-2'} />
                                                Refresh Calls
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
                <Col lg={4}>
                    <CallCenterCampaignQueue teamId={teamId} teams={teams} />
                </Col>
            </Row>
            {showActiveCallModal && (
                <ActiveCallModal
                    show={showActiveCallModal}
                    onClose={() => {
                        setShowActiveCallModal(false);
                        setActiveCallId(null);
                        setActiveCall(null);
                    }}
                    activeCallId={activeCallId}
                    onHangup={onHangup}
                    onListenIn={onListenIn}
                    onBargeIn={onBargeIn}
                    bargeLoading={bargeLoading}
                    listenLoading={listenLoading}
                    hangupLoading={hangupLoading}
                />
            )}
        </StyledCalledTab>
    );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = {
    setActiveCall,
    fetchCallsAction,
};

const ActiveCallsTab = (props) => (
    <ComponentErrorBoundary componentErrorMessage={props?.componentErrorMessage}>
        <Component {...props} />
    </ComponentErrorBoundary>
);

export default connect(mapStateToProps, mapDispatchToProps)(ActiveCallsTab);
